// Eskom PARCC — one editable finding line (shared).
//
// Extracted from TransactionEditor so BOTH the transaction editor and the
// working-paper editor capture findings the same way: a finding typed here is
// classified by the rulebook (anchors + suggested compliance + matched rule +
// regulatory references) and persisted to the Finding table, so it reaches the
// Findings Register regardless of where it was captured (owner feedback 2).
//
// Pure-presentational: it owns no state and no write — the parent holds the
// draft list and the save sequence. The regulatory references are shown
// READ-ONLY (rulebook-derived, item 1); the reviewer edits text / compliance /
// anchors / rationale / notes.
import { Button, Select } from '../../components/ui';
import { SELECTABLE_COMPLIANCE_LEVELS, GAP_STATUS_OPTIONS, ANCHORS } from '../../domain/enums';
import { normalizeFindingStatus } from '../../domain/normalize';
import type { FindingDraft } from '../transactions/editTransaction';
import type { RuleReferences } from '../../domain/rulebook/ruleReferences';

/** The non-empty reference groups for display, labelled. */
function refGroups(refs: RuleReferences | undefined): { label: string; items: string[] }[] {
  if (!refs) return [];
  return [
    { label: 'Eskom Policy', items: refs.eskomPolicy ?? [] },
    { label: 'PFMA', items: refs.pfma ?? [] },
    { label: 'Law / Regulation', items: refs.law ?? [] },
  ].filter((g) => g.items.length > 0);
}

export function FindingEditorCard({
  draft: d,
  disabled,
  onText,
  onPatch,
  onToggleAnchor,
  onDelete,
}: {
  draft: FindingDraft;
  disabled: boolean;
  onText: (text: string) => void;
  onPatch: (patch: Partial<FindingDraft>) => void;
  onToggleAnchor: (anchor: string, checked: boolean) => void;
  onDelete: () => void;
}): React.JSX.Element {
  const suggestionMeta = [
    d.route && `Route: ${d.route.toUpperCase()}`,
    d.confidence && `Confidence: ${d.confidence}`,
    d.suggestedCompliance && `Suggested: ${d.suggestedCompliance}`,
  ].filter((v): v is string => typeof v === 'string' && v.length > 0);

  const groups = refGroups(d.regulatoryRefs);

  return (
    <div className="finding-card">
      <div className="finding-head">
        <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span className="small">Finding text</span>
          <textarea
            value={d.text}
            disabled={disabled}
            onChange={(e) => onText(e.target.value)}
            placeholder="Describe the finding…"
          />
        </label>
        <Button variant="ghost" onClick={onDelete} disabled={disabled} aria-label="Delete finding">
          Delete
        </Button>
      </div>

      {suggestionMeta.length > 0 && (
        <div className="small" style={{ margin: '6px 0' }}>
          {suggestionMeta.join(' · ')}
        </div>
      )}

      <div className="detail-edit-grid" style={{ margin: '8px 0' }}>
        <label>
          Final compliance
          <Select
            ariaLabel="Final compliance"
            value={d.finalCompliance}
            disabled={disabled}
            onChange={(v) => onPatch({ finalCompliance: v })}
            options={[
              { value: '', label: '— (use suggestion)' },
              ...SELECTABLE_COMPLIANCE_LEVELS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
        <label>
          {/* The four-state gap-closure lifecycle (9 Jun call). Closing a gap here
              propagates to the transaction/dashboard via the save-time rollup. */}
          Status (gap closure)
          <Select
            ariaLabel="Status (gap closure)"
            value={normalizeFindingStatus(d.effectiveStatus ?? '')}
            disabled={disabled}
            onChange={(v) => onPatch({ effectiveStatus: v })}
            options={[
              { value: '', label: '—' },
              ...GAP_STATUS_OPTIONS.map((o) => ({ value: o, label: o })),
            ]}
          />
        </label>
      </div>

      <fieldset className="anchor-list" style={{ border: 0, padding: 0, margin: '4px 0 8px' }}>
        {ANCHORS.map((anchor) => {
          const checked = d.anchors.includes(anchor);
          return (
            <label
              key={anchor}
              className="anchor"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onToggleAnchor(anchor, e.target.checked)}
              />
              {anchor}
            </label>
          );
        })}
      </fieldset>

      {/* Rulebook-derived regulatory references (item 1) — read-only. Shown only
          when the matched rule carries them; flagged as draft pending sign-off. */}
      {groups.length > 0 && (
        <div className="finding-refs" style={{ margin: '4px 0 8px' }}>
          <div className="small" style={{ fontWeight: 700 }}>
            Regulatory references{' '}
            <span style={{ fontWeight: 400, opacity: 0.7 }}>(rulebook · draft)</span>
          </div>
          {groups.map((g) => (
            <div key={g.label} className="small" style={{ marginTop: 2 }}>
              <span style={{ fontWeight: 600 }}>{g.label}:</span> {g.items.join('; ')}
            </div>
          ))}
        </div>
      )}

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
        <span className="small">Rationale</span>
        <textarea
          value={d.rationale}
          disabled={disabled}
          onChange={(e) => onPatch({ rationale: e.target.value })}
        />
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="small">Review notes</span>
        <textarea
          value={d.reviewNotes}
          disabled={disabled}
          onChange={(e) => onPatch({ reviewNotes: e.target.value })}
        />
      </label>
    </div>
  );
}
